import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Menu, X } from "lucide-react";
import Logo from "@/components/Logo";

const LINKS = [
    { href: "#services", label: "Services" },
    { href: "#tiers", label: "Creators" },
    { href: "#engine", label: "Engine" },
    { href: "#process", label: "Process" },
    { href: "#why", label: "Why FocS" },
];

export default function Nav() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "border-b border-white/10 bg-ink/85 backdrop-blur-xl"
                    : "bg-transparent"
            }`}
        >
            <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8">
                <Logo />

                <div className="hidden items-center gap-8 lg:flex">
                    {LINKS.map((l) => (
                        <a
                            key={l.href}
                            href={l.href}
                            data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className="text-sm font-medium text-slate-300 transition-colors hover:text-white"
                        >
                            {l.label}
                        </a>
                    ))}
                </div>

                <div className="flex items-center gap-3">
                    <a
                        href="#contact"
                        data-testid="nav-cta"
                        className="group hidden items-center gap-2 rounded-full bg-gradient-to-r from-crimson to-flame px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-crimson/25 transition-transform duration-300 hover:scale-[1.03] sm:inline-flex"
                    >
                        Start a Campaign
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                    </a>
                    <button
                        data-testid="nav-mobile-toggle"
                        aria-label="Toggle menu"
                        onClick={() => setOpen((v) => !v)}
                        className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-white lg:hidden"
                    >
                        {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </button>
                </div>
            </nav>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden border-b border-white/10 bg-ink/95 backdrop-blur-xl lg:hidden"
                    >
                        <div className="flex flex-col gap-1 px-5 py-4">
                            {LINKS.map((l) => (
                                <a
                                    key={l.href}
                                    href={l.href}
                                    onClick={() => setOpen(false)}
                                    data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                    className="rounded-lg px-3 py-3 text-base font-medium text-slate-200 hover:bg-white/5"
                                >
                                    {l.label}
                                </a>
                            ))}
                            <a
                                href="#contact"
                                onClick={() => setOpen(false)}
                                data-testid="nav-mobile-cta"
                                className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-crimson to-flame px-5 py-3 text-sm font-semibold text-white"
                            >
                                Start a Campaign
                                <ArrowRight className="h-4 w-4" />
                            </a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}
